'use client';

import { useEffect, useRef, useTransition } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  holdThroughNavigation,
  refreshLanded,
  registerRefreshRunner,
  releaseNavigationHold,
} from '@/lib/refresh-gate';

/**
 * The refresh coordinator's hands in the page (src/lib/refresh-gate.ts),
 * mounted once in the root layout so every page has it, signed in or not.
 * Renders nothing.
 *
 * - **It runs the refresh.** The coordinator decides when; this component owns
 *   the router and starts `router.refresh()` inside its own transition. React
 *   commits the transition's `pending: false` together with the router update
 *   it entangles, so the moment `refreshing` falls back is the moment the
 *   refreshed tree is on screen, and that is what it reports as landed.
 * - **It arms the navigation hold.** A click on an in-app link holds every
 *   refresh until the route it asked for has landed (the gate's hold 2), and a
 *   pathname change releases it.
 */
export function RefreshHost() {
  const router = useRouter();
  const pathname = usePathname();
  const [refreshing, startRefresh] = useTransition();
  const running = useRef(false);

  useEffect(
    () =>
      registerRefreshRunner(() => {
        running.current = true;
        // eslint-disable-next-line no-restricted-syntax -- the coordinator's runner: the one sanctioned caller (src/lib/refresh-gate.ts)
        startRefresh(() => router.refresh());
      }),
    [router],
  );

  useEffect(() => {
    if (refreshing || !running.current) return;
    running.current = false;
    refreshLanded();
  }, [refreshing]);

  useEffect(() => {
    // Arm the navigation hold on the way INTO a route change, in the capture
    // phase: React's own click handling is delegated to the root container in
    // the bubble phase, so this runs before the Link that is about to
    // navigate. Only the clicks a Link would take: primary button, no
    // modifier, same origin, this tab. Read through the attributes rather than
    // the anchor's properties, so an SVG link (the map's nodes) counts too.
    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target;
      const anchor = target instanceof Element ? target.closest('a') : null;
      const href = anchor?.getAttribute('href');
      if (!anchor || href === null || href === undefined) return;
      if (anchor.hasAttribute('download')) return;
      const linkTarget = anchor.getAttribute('target');
      if (linkTarget && linkTarget !== '_self') return;
      try {
        if (new URL(href, location.href).origin !== location.origin) return;
      } catch {
        return;
      }
      holdThroughNavigation();
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, []);

  // The route the click asked for has landed: release the hold and let a
  // refresh that waited on it through.
  useEffect(() => {
    releaseNavigationHold();
  }, [pathname]);

  return null;
}
